import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import TableView, {
  ColumnConfig,
  FetchParams,
} from "../../../components/controls/table-view";
import {
  SmallTextContainer,
  TextContainer,
} from "../../../styles/shared/table";
import Button from "../../../components/controls/button";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../hooks/useAppSelector";
import {
  deleteProduct,
  deleteProductOverride,
  fetchSupplierProducts,
  fetchSupplierProductComparisons,
  insertProductOverride,
  resetSupplierProducts,
  restoreSupplierProduct,
  resetComparisonProducts,
} from "../../../store/features/supplier/supplier-product-slice";
import {
  ProductContainer,
  ProductDescription,
  ProductImage,
  ProductNameContainer,
  ProductOverride,
  ProductTitle,
} from "../../../styles/shared/product";
import Image from "../../../components/controls/image";
import {
  GoogleIcon,
  ICON_ID,
  ICON_SIZES,
} from "../../../components/icons/google-icon";
import { currencyFormatter } from "../../../helpers/formatters";
import styled from "styled-components";
import { openDrawer } from "../../../store/features/drawer/drawer-slice";
import { DrawerID } from "../../../constants/drawer-constants";
import { closeConfirm, openConfirm } from "../../../store/features/notification/confirm-slice";
import LoadingSpinner from "../../../components/controls/loading-spinner";
import { ApplicationSupplierProductHeader } from "../../../models/supplier/application-supplier-product-header";

interface SupplierProductProps {
  supplierId: number;
}
export default function SupplierProducts({ supplierId }: SupplierProductProps) {
  const products = useAppSelector((state) => state.supplierProducts);
  const supplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const dispatch = useAppDispatch();
  const [comparisonAnchorId, setComparisonAnchorId] = useState<number | null>(null);
  const [comparisonPosition, setComparisonPosition] = useState({ top: 0, left: 0 });
  const [comparisonAnchorRect, setComparisonAnchorRect] = useState<DOMRect | null>(null);

  const closeComparison = () => {
    setComparisonAnchorId(null);
    setComparisonAnchorRect(null);
    dispatch(resetComparisonProducts());
  };

  useEffect(() => {
    fetchMoreData({
      reset: true
    });
    return () => {
      dispatch(resetComparisonProducts());
      dispatch(resetSupplierProducts());
    };
  }, []);

  const fetchMoreData = async (fetchParams: FetchParams) => {
    await dispatch(fetchSupplierProducts({ supplierId, unmappedOnly: false, fetchParams }));
  };

  const updateComparisonPosition = (anchorRect: DOMRect, estimatedHeight: number) => {
    const viewportPadding = 12;
    const gap = 8;
    const popoverWidth = 720;

    const spaceBelow = window.innerHeight - anchorRect.bottom - viewportPadding;
    const spaceAbove = anchorRect.top - viewportPadding;
    const renderAbove = spaceBelow < estimatedHeight && spaceAbove >= estimatedHeight;

    const top = renderAbove
      ? Math.max(viewportPadding, anchorRect.top - estimatedHeight - gap)
      : Math.min(anchorRect.bottom + gap, window.innerHeight - estimatedHeight - viewportPadding);

    const idealLeft = anchorRect.left;
    const maxLeft = window.innerWidth - popoverWidth - viewportPadding;

    setComparisonPosition({
      top: Math.max(viewportPadding, top),
      left: Math.max(viewportPadding, Math.min(idealLeft, maxLeft)),
    });
  };

  const toggleComparison = async (
    row: ApplicationSupplierProductHeader,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();

    if (!row.productId) {
      return;
    }

    if (comparisonAnchorId === row.id) {
      closeComparison();
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const estimatedHeight = 120;

    setComparisonAnchorId(row.id);
    setComparisonAnchorRect(bounds);
    updateComparisonPosition(bounds, estimatedHeight);
    dispatch(resetComparisonProducts());
    await dispatch(fetchSupplierProductComparisons(row.productId ?? row.id));
  };

  useEffect(() => {
    if (!comparisonAnchorRect || comparisonAnchorId === null) {
      return;
    }

    const estimatedHeight = products.comparisonLoading
      ? 170
      : 96 + Math.max(1, products.comparisonProducts?.length ?? 0) * 34;

    updateComparisonPosition(comparisonAnchorRect, estimatedHeight);
  }, [comparisonAnchorId, comparisonAnchorRect, products.comparisonLoading, products.comparisonProducts]);

  useEffect(() => {
    if (comparisonAnchorId === null) {
      return;
    }

    const handleScroll = () => {
      closeComparison();
    };

    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [comparisonAnchorId]);

  const renderActionButton = (row: any) => (
    <div>
      <Button
        variant="contained"
        id={row.name}
        label="Edit"
        isPrimary={true}
        fullWidth={false}
        onClick={() =>
          dispatch(
            openDrawer({
              id: DrawerID.SupplierProduct,
              anchor: "right",
              data: {
                id: row.id,
                headerText: `Edit ${row.name}`,
              },
            })
          )
        }
      />
      <Button
        variant="text"
        id={`delete_${row.id}`}
        label=""
        icon={ICON_ID.Delete}
        isDanger={true}
        fullWidth={false}
        onClick={() => handleDeleteClick(row)}
      />

    </div>
  );

  const columnConfig: { [key: string]: ColumnConfig } = {

    productId: {
      hidden: true,
    },
    supplierName: {
      hidden: true,
    },
    supplierId: {
      hidden: true,
    },
    size: {
      hidden: true,
    },
    description: {
      hidden: true,
    },
    image: {
      hidden: true,
    },
    openPricing: {
      hidden: true,
    },
    plu: {
      hidden: true,
    },
    sizeUnit: {
      hidden: true,
    },
    reference: {
      hidden: true,
    },
    deliveryDay: {
      hidden: true
    },
    information: {
      hidden: true
    },
    className: {
      hidden: true
    },
    name: {
      order: 1,
      name: "Product",
      colspan: 3,
      render: (value: string, row: any) => (
        <ProductContainer>
          <ProductImage>
            {row.productId ? (
              <Image image={row.image}></Image>
            ) : (
              <GoogleIcon
                tooltip="Not mapped"
                id={ICON_ID.QuestionMark}
                isWarning
                size={ICON_SIZES.Large}
              />
            )
            }
          </ProductImage >
          <ProductNameContainer>
            <ProductTitle>
              {value} {row.reference ? ` - (${row.reference})` : ""}
            </ProductTitle>
            <ProductDescription>
              {row.size} {row.sizeUnit}
              {row.description && " - " + row.description}
            </ProductDescription>
            {row.plu && <SmallTextContainer>Plu: {row.plu}</SmallTextContainer>}
          </ProductNameContainer>
          <ComparisonWrapper>
            <Button
              variant="text"
              id={`compare_${row.id}`}
              label="Compare"
              icon={ICON_ID.ViewList}
              isSecondary={true}
              fullWidth={false}
              disabled={!row.productId}
              onClick={(event) => toggleComparison(row, event)}
            />

            {comparisonAnchorId === row.id && typeof document !== "undefined"
              ? createPortal(
                  <ComparisonTooltip $top={comparisonPosition.top} $left={comparisonPosition.left}>
                    <ComparisonTooltipHeader>
                      <ComparisonTooltipTitle>{row.name} price comparison</ComparisonTooltipTitle>
                      <Button
                        variant="text"
                        id={`close_compare_${row.id}`}
                        label=""
                        icon={ICON_ID.Close}
                        isNeutral={true}
                        fullWidth={false}
                        onClick={() => {
                          closeComparison();
                        }}
                      />
                    </ComparisonTooltipHeader>

                    {products.comparisonLoading && products.comparisonProductId === row.id ? (
                      <ComparisonLoading>
                        <LoadingSpinner />
                      </ComparisonLoading>
                    ) : (
                      <ComparisonGrid>
                        <ComparisonGridHeader>Supplier</ComparisonGridHeader>
                        <ComparisonGridHeader>Price</ComparisonGridHeader>
                        <ComparisonGridHeader>Size</ComparisonGridHeader>
                        <ComparisonGridHeader>Description</ComparisonGridHeader>
                        {(products.comparisonProducts ?? []).map((comparisonRow) => {
                          const isCurrentSupplier = comparisonRow.supplierId === row.supplierId;

                          return (
                            <ComparisonGridRow key={`${row.id}_${comparisonRow.id}`}>
                              <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                {comparisonRow.supplierName || "-"}
                              </ComparisonGridValue>
                              <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                {comparisonRow.openPricing ? "TBC" : comparisonRow.price != null ? currencyFormatter.format(comparisonRow.price) : "-"}
                              </ComparisonGridValue>
                              <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                {comparisonRow.size != null
                                  ? `${comparisonRow.size} ${comparisonRow.sizeUnit ?? ""}`.trim()
                                  : "-"}
                              </ComparisonGridValue>
                              <ComparisonGridValue $isCurrentSupplier={isCurrentSupplier}>
                                {comparisonRow.description || "-"}
                              </ComparisonGridValue>
                            </ComparisonGridRow>
                          );
                        })}
                      </ComparisonGrid>
                    )}
                  </ComparisonTooltip>,
                  document.body
                )
              : null}
          </ComparisonWrapper>
        </ProductContainer >
      ),
    },
    origin: {
      order: 2,
    },
    price: {
      order: 3,
      render: (value: number | null, row) => (
        <TextContainer>
          {row.openPricing ? "TBC" : (value && (currencyFormatter.format(value)))}
        </TextContainer >
      )
    },
    minimumOrderQuantity: {
      hidden: true,
    },
    active: {
      order: 5,
      hideOnMobile: true,
      render: (value: boolean) => (
        <TextContainer>
          {value ? (
            <GoogleIcon
              tooltip="Active"
              id={ICON_ID.CheckCircle}
              size={ICON_SIZES.Large}
              isSuccess
            />
          ) : (
            <GoogleIcon
              tooltip="Disabled"
              id={ICON_ID.Close}
              size={ICON_SIZES.Large}
              isDanger
            />
          )}
        </TextContainer>
      ),
    },
    updated: {
      order: 6,
      hideOnMobile: true,
      render: (value: Date) => (
        <TextContainer>
          {new Date(value).toLocaleDateString()}{" "}
          {new Date(value).toLocaleTimeString()}
        </TextContainer>
      ),
    },
    layerSize:{
      hidden: supplier?.productOrderingType !== "Pallets",
      name: "Pallet Configuration",
      order: 4,
      render: (value: number | null, row: any) => (
        <TextContainer>
          {row.palletSize && row.layerSize ? `${row.layerSize} per layer, ${row.palletSize} layers` : "-"}
        </TextContainer>
      )
    },
    palletSize:{
      hidden: true
    },
    hasHistory: {
      name: "",
      order: 7,
      colspan: 2,
      render: (value: boolean, row: any) => {
        if (value) {
          return <Button
            variant="outlined"
            id={`undo_${row.name}`}
            label="Undo Last Change"
            isPrimary={true}
            fullWidth={false}
            onClick={() =>
              dispatch(restoreSupplierProduct({ supplierProductId: row.id }))
            }
          />
        }
        return <></>
      }
    },
    productOverride: {
      order: 8,
      name: "",
      render: (value: any, row: any) => {
        return <ProductOverride>
          {row.productOverride ?
            <SmallTextContainer>
              {row.productOverride.supplierId != row.supplierId ?
                <>
                  Override Active for {row.productOverride.supplierName}
                </>
                :
                <Button
                  id="removeOverride"
                  variant="text"
                  isWarning
                  label="Remove Override"
                  onClick={async () => {
                    await dispatch(deleteProductOverride({ productOverrideId: row.productOverride.id }))
                  }}
                />
              }

            </SmallTextContainer>
            : (row.active &&
              <Button
                id="btnSetOverride"
                variant="text"
                isWarning
                label="Set Override"
                onClick={async () => {
                  await dispatch(insertProductOverride({ supplierId, productId: row.productId }))
                }}
              ></Button>)
          }
        </ProductOverride>
      }
    },
    id: {
      hidden: true
    }
  }

  const handleDeleteClick = (row: any) => {
    const onConfirm = () => {
      dispatch(deleteProduct(row.id));
      dispatch(closeConfirm());
    };

    const onCancel = () => {
      dispatch(closeConfirm());
    };

    dispatch(
      openConfirm({
        title: "Remove " + row.name + "?",
        message:
          "Are you sure you want to delete this product?",
        show: true,
        data: {
          onConfirm: onConfirm,
          onCancel: onCancel,
        },
      })
    );
  };
  return (
    <TableContainer>
      <TableView
        id="tblProducts"
        emptyText="No products"
        preLoadedData={true}
        fetchData={(search) => fetchMoreData(search ?? false)}
        totalCount={products.products?.count ?? 0}
        data={products?.products?.products ?? []}
        columnConfig={columnConfig}
        actionButton={renderActionButton}
        showHeaders={true}
        search={true}
      />
    </TableContainer >
  );
}

const TableContainer = styled.div`
  min-height: 0; 
`;

const ComparisonWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
`;

const ComparisonTooltip = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => `${$top}px`};
  left: ${({ $left }) => `${$left}px`};
  z-index: 50;
  min-width: min(520px, 72vw);
  max-width: min(720px, 88vw);
  padding: 12px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.16);
  box-shadow: 0 18px 40px rgba(7, 94, 84, 0.18);
`;

const ComparisonTooltipHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

const ComparisonTooltipTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
`;

const ComparisonLoading = styled.div`
  padding: 18px 0;
  display: flex;
  justify-content: center;
`;

const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.8fr 0.75fr 1.2fr;
  gap: 8px;
`;

const ComparisonGridHeader = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #54656f;
  border-bottom: 1px solid rgba(7, 94, 84, 0.1);
  padding-bottom: 6px;
`;

const ComparisonGridRow = styled.div`
  display: contents;
`;

const ComparisonGridValue = styled.div<{ $isCurrentSupplier?: boolean }>`
  font-size: 0.82rem;
  line-height: 1.4;
  padding: 4px 0;
  border-bottom: 1px solid rgba(7, 94, 84, 0.06);
  font-weight: ${({ $isCurrentSupplier }) => ($isCurrentSupplier ? 700 : 400)};
  background: ${({ $isCurrentSupplier }) => ($isCurrentSupplier ? "rgba(37, 211, 102, 0.12)" : "transparent")};
  color: ${({ $isCurrentSupplier }) => ($isCurrentSupplier ? "#0b3f38" : "inherit")};
`;
